import { useMemo, useState } from "react";
import {
  Bot,
  Download,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Scissors,
  Trash2,
  Upload,
  UserRound,
  FlaskConical,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { ApiError } from "@/shared/lib/apiClient";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { FormField, inputClass } from "../components/FormField";
import {
  classifyHospitalMedicalActs,
  createHospitalMedicalAct,
  deleteHospitalMedicalAct,
  importHospitalMedicalActsCsv,
  updateHospitalMedicalAct,
} from "../api/hospitalApi";
import { getAuthToken } from "../auth";
import {
  providerMedicalActsCsvExample,
  providerMedicalActsStats,
} from "../data";
import { useHospitalMedicalActs } from "../hooks/useHospitalMedicalActs";
import { cn } from "@/shared/lib/cn";
import type { ProviderMedicalAct, ProviderMedicalActCareType } from "../types";

const careTypeStyles: Record<
  ProviderMedicalActCareType,
  { className: string; icon: typeof Plus }
> = {
  pharmacy: { className: "bg-green/15 text-green", icon: Link2 },
  nursing: { className: "bg-teal-soft text-teal", icon: Plus },
  consultation: { className: "bg-card-muted text-muted-foreground", icon: UserRound },
  surgery: { className: "bg-pink-soft text-pink", icon: Scissors },
  imaging: { className: "bg-purple-soft text-purple", icon: FlaskConical },
  laboratory: { className: "bg-teal-soft text-teal", icon: FlaskConical },
  imagingLab: { className: "bg-purple-soft text-purple", icon: FlaskConical },
};

type CreateActForm = {
  code: string;
  label: string;
  cost: string;
};

const emptyForm: CreateActForm = {
  code: "",
  label: "",
  cost: "",
};

function formatCost(value: number): string {
  return `${value.toFixed(2)} USD`;
}

export function HospitalMedicalActsPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.acts.medicalActs;
  const { showToast } = usePageActions();
  const { acts, setActs, loading, error, usingApi, reload } = useHospitalMedicalActs();

  const [importFileName, setImportFileName] = useState(labels.import.noFileSelected);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [createForm, setCreateForm] = useState<CreateActForm>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [classifying, setClassifying] = useState(false);

  const pendingCount = useMemo(
    () => acts.filter((act) => act.pendingValidation).length,
    [acts],
  );

  const totalCount = acts.length;

  const filteredActs = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return acts;
    return acts.filter(
      (act) =>
        act.label.toLowerCase().includes(query) || act.code.toLowerCase().includes(query),
    );
  }, [acts, search]);

  const totalPages = Math.max(1, Math.ceil(filteredActs.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = filteredActs.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageActs = filteredActs.slice(start, start + pageSize);
  const from = filteredActs.length === 0 ? 0 : start + 1;
  const to = Math.min(start + pageSize, filteredActs.length);

  const resetForm = () => {
    setEditingId(null);
    setCreateForm(emptyForm);
  };

  const handleDownloadExample = () => {
    const blob = new Blob([providerMedicalActsCsvExample], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "exemple-actes-medicaux.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const runClassifyAi = async () => {
    if (!getAuthToken() || classifying) return;
    setClassifying(true);
    try {
      const result = await classifyHospitalMedicalActs({ force: false, limit: 45 });
      const partial = result.truncated ? " (lot partiel — relancez pour continuer)" : "";
      showToast(
        `${result.message ?? "Classification terminée."} ${result.classified} classé(s), ${result.skipped} ignoré(s).${partial}`,
      );
      await reload();
    } catch (err) {
      const message =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : "Classification IA impossible.";
      showToast(message);
    } finally {
      setClassifying(false);
    }
  };

  const handleImport = async () => {
    if (importFileName === labels.import.noFileSelected || !importFile) {
      showToast({ message: labels.import.selectFileFirst, variant: "warning" });
      return;
    }

    if (!getAuthToken()) {
      showToast(labels.import.success);
      return;
    }

    setImporting(true);
    try {
      const result = await importHospitalMedicalActsCsv(importFile);
      await reload();
      const detail =
        result.errors?.length > 0
          ? ` (${result.imported} importés, ${result.duplicates} doublons)`
          : ` (${result.imported} importés)`;
      showToast(`${labels.import.success}${detail}`);
      setImportFile(null);
      setImportFileName(labels.import.noFileSelected);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : labels.import.selectFileFirst;
      showToast({ message, variant: "warning" });
    } finally {
      setImporting(false);
    }
  };

  const handleEdit = (act: ProviderMedicalAct) => {
    setEditingId(act.id);
    setCreateForm({
      code: act.code,
      label: act.label,
      cost: String(act.costUsd),
    });
  };

  const handleCreate = async () => {
    const cost = Number.parseFloat(createForm.cost.replace(",", "."));
    if (!createForm.code.trim() || !createForm.label.trim() || Number.isNaN(cost)) {
      showToast({ message: labels.create.validationError, variant: "warning" });
      return;
    }

    const duplicate = acts.some(
      (act) =>
        act.code.toLowerCase() === createForm.code.trim().toLowerCase() &&
        act.id !== editingId,
    );
    if (duplicate) {
      showToast({ message: labels.create.duplicateCode, variant: "warning" });
      return;
    }

    if (!getAuthToken()) {
      if (editingId) {
        setActs((current) =>
          current.map((act) =>
            act.id === editingId
              ? {
                  ...act,
                  code: createForm.code.trim(),
                  label: createForm.label.trim(),
                  costUsd: cost,
                }
              : act,
          ),
        );
      } else {
        const newAct: ProviderMedicalAct = {
          id: `pma-${Date.now()}`,
          code: createForm.code.trim(),
          label: createForm.label.trim(),
          careType: "nursing",
          costUsd: cost,
          createdAt: new Date().toLocaleDateString("fr-FR"),
          pendingValidation: true,
        };
        setActs((current) => [newAct, ...current]);
      }
      resetForm();
      setPage(1);
      showToast(labels.create.success);
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateHospitalMedicalAct(Number(editingId), {
          code: createForm.code.trim(),
          label: createForm.label.trim(),
          cost,
        });
      } else {
        await createHospitalMedicalAct({
          code: createForm.code.trim(),
          label: createForm.label.trim(),
          cost,
        });
      }
      resetForm();
      setPage(1);
      await reload();
      showToast(labels.create.success);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : labels.create.validationError;
      showToast({ message, variant: "warning" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!getAuthToken()) {
      setActs((current) => current.filter((act) => act.id !== id));
      showToast(labels.list.deleteSuccess);
      return;
    }

    try {
      await deleteHospitalMedicalAct(Number(id));
      await reload();
      if (editingId === id) resetForm();
      showToast(labels.list.deleteSuccess);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Suppression impossible.";
      showToast({ message, variant: "warning" });
    }
  };

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/hospital"
        items={[{ label: labels.pageTitle }]}
      />

      <div className="mb-5">
        <h1 className="text-xl font-bold text-foreground">{labels.pageTitle}</h1>
        {(usingApi || error || loading) && (
          <p className={`mt-1 text-xs ${error ? "text-pink" : "text-muted"}`}>
            {error ?? (loading ? "Chargement…" : `${acts.length} acte(s) API`)}
          </p>
        )}
      </div>

      <div className="theme-card mb-5 p-5 md:p-6">
        <div className="mb-4 flex items-center gap-2">
          <Upload size={18} className="text-primary" />
          <h2 className="text-base font-semibold text-foreground">{labels.import.title}</h2>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <FormField label={labels.import.fileLabel}>
              <label className="flex cursor-pointer flex-wrap items-center gap-3">
                <span className="inline-flex rounded-lg border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent">
                  {labels.import.chooseFile}
                </span>
                <span className="text-sm text-muted">{importFileName}</span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(event) => {
                    const file = event.target.files?.[0] ?? null;
                    setImportFile(file);
                    setImportFileName(file?.name ?? labels.import.noFileSelected);
                  }}
                />
              </label>
              <p className="mt-2 text-xs text-muted">{labels.import.formatHint}</p>
            </FormField>
            <button
              type="button"
              onClick={() => void handleImport()}
              disabled={importing}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60 sm:w-auto"
            >
              <Upload size={16} />
              {labels.import.submit}
            </button>
          </div>

          <div className="flex flex-col items-start gap-2 lg:items-end">
            <button
              type="button"
              onClick={handleDownloadExample}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-accent/20 hover:border-accent"
            >
              <Download size={16} />
              {labels.import.downloadExample}
            </button>
            <p className="text-xs text-muted">{labels.import.duplicateHint}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="theme-card p-5 md:p-6 xl:col-span-1">
          <h2 className="mb-5 text-base font-semibold text-foreground">
            {editingId ? "Modifier un acte" : labels.create.title}
          </h2>
          <form
            className="space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              void handleCreate();
            }}
          >
            <FormField label={labels.create.fields.code} required>
              <input
                value={createForm.code}
                onChange={(event) =>
                  setCreateForm((current) => ({ ...current, code: event.target.value }))
                }
                placeholder={labels.create.placeholders.code}
                className={inputClass}
                required
              />
            </FormField>
            <FormField label={labels.create.fields.label} required>
              <input
                value={createForm.label}
                onChange={(event) =>
                  setCreateForm((current) => ({ ...current, label: event.target.value }))
                }
                placeholder={labels.create.placeholders.label}
                className={inputClass}
                required
              />
            </FormField>
            <FormField label={labels.create.fields.cost} required>
              <input
                value={createForm.cost}
                onChange={(event) =>
                  setCreateForm((current) => ({ ...current, cost: event.target.value }))
                }
                placeholder={labels.create.placeholders.cost}
                className={inputClass}
                required
              />
            </FormField>
            <div className="flex flex-col gap-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
              >
                {editingId ? <Pencil size={16} /> : <Plus size={16} />}
                {saving ? "…" : editingId ? "Enregistrer" : labels.create.submit}
              </button>
              {editingId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="inline-flex w-full items-center justify-center rounded-lg border border-border px-4 py-2 text-sm"
                >
                  Annuler
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="theme-card overflow-hidden xl:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-foreground">{labels.list.title}</h2>
              <span className="inline-flex rounded-md bg-primary px-2 py-0.5 text-xs font-bold text-white">
                {totalCount}
              </span>
              {pendingCount > 0 && (
                <span className="inline-flex rounded-md bg-yellow px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  {labels.list.pendingBadge.replace("{count}", String(pendingCount))}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={classifying}
                onClick={() => void runClassifyAi()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
                title="Classification Gemini (GEMINI_ENABLED + GEMINI_API_KEY requis)"
              >
                {classifying ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />}
                {labels.list.ai.replace("{count}", String(providerMedicalActsStats.aiBatchSize))}
              </button>
            </div>
          </div>

          <TableToolbar
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            searchPlaceholder={labels.list.searchPlaceholder}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            showLabel={t.safecheck.show}
            entriesLabel={t.safecheck.entries}
          />

          <div className="max-h-[560px] overflow-x-auto overflow-y-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-border bg-card-muted/50">
                  {Object.values(labels.list.columns).map((header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageActs.map((act) => {
                  const careStyle = careTypeStyles[act.careType] ?? careTypeStyles.consultation;
                  const CareIcon = careStyle.icon;

                  return (
                    <tr
                      key={act.id}
                      className="border-b border-border last:border-0 hover:bg-card-muted/30"
                    >
                      <td className="px-4 py-3 text-sm font-medium uppercase text-foreground">
                        {act.label}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex max-w-[220px] items-center gap-1.5 rounded-md px-2 py-1 text-[10px] font-semibold uppercase leading-tight",
                            careStyle.className,
                          )}
                        >
                          <CareIcon size={12} className="shrink-0" />
                          {labels.careTypes[act.careType] ?? act.careType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground">{formatCost(act.costUsd)}</td>
                      <td className="px-4 py-3 text-sm text-muted">{act.createdAt}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(act)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:bg-accent/20 hover:border-accent"
                            aria-label="Modifier"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(act.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-pink text-white transition-colors hover:bg-accent hover:text-accent-foreground"
                            aria-label={labels.list.delete}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4">
            <p className="text-sm text-muted">
              {labels.list.pagination
                .replace("{from}", String(from))
                .replace("{to}", String(to))
                .replace("{total}", String(filteredActs.length))}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={currentPage === 1}
                className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
              >
                {labels.list.previous}
              </button>
              <span className="text-sm text-muted">
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={currentPage === totalPages}
                className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:cursor-not-allowed disabled:opacity-40"
              >
                {labels.list.next}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
